import { Controller } from "@hotwired/stimulus"
import { debug } from "../debug"

// Add debug log when this file is loaded
debug('iframe_loader_controller.js file loaded');
debug("IframeLoaderController loading...");

export default class extends Controller {
  static targets = ['iframe', 'loading', 'error']
  static values = {
    timeout: { type: Number, default: 10000 }, // 10 seconds timeout
    src: String
  }

  initialize() {
    // Keep references to bound methods so they can be removed later
    this.handleTurboBeforeVisit = this.beforeVisit.bind(this)
    this.handleTurboLoad = this.turboLoad.bind(this)
    
    // Add Turbo event listeners
    document.addEventListener('turbo:before-visit', this.handleTurboBeforeVisit)
    document.addEventListener('turbo:load', this.handleTurboLoad)
    
    debug('Iframe loader controller initialized')
  }

  connect() {
    debug('Iframe loader controller connected - element:', this.element)
    debug('Has iframe target:', this.hasIframeTarget)
    debug('Has loading target:', this.hasLoadingTarget)
    debug('Has error target:', this.hasErrorTarget)
    
    // Set initial state
    this.showLoading()
    
    // Start loading process with a clean slate
    this.startLoading()
  }

  disconnect() {
    debug('Iframe loader controller disconnected')
    this.clearAllTimers()
  }
  
  // Turbo navigation handlers
  beforeVisit() {
    debug('Turbo navigation started, cleaning up iframe loader')
    this.clearAllTimers()
  }
  
  turboLoad() {
    debug('Turbo load event, checking if iframe loader needs reinitializing')
    if (this.element && this.element.isConnected) {
      debug('Iframe loader still connected after Turbo navigation, reinitializing')
      this.startLoading()
    }
  }
  
  // Main loading process
  startLoading() {
    // Clear any existing timers
    this.clearAllTimers()
    
    // Show loading state
    this.showLoading()
    
    // Start a timer to show the iframe after a delay
    // This ensures users see a loading indicator even if the iframe loads instantly
    this.shortDelayTimeout = setTimeout(() => {
      debug('Showing iframe after initial delay')
      this.showIframe()
    }, 1500)
    
    // Set up a backup timeout in case the iframe never loads
    this.timeoutId = setTimeout(() => {
      debug('Loading timeout reached, showing error')
      this.showError()
    }, this.timeoutValue || 10000)
    
    // If using a fixed src from data attribute
    if (this.hasSrcValue && this.srcValue && this.hasIframeTarget) {
      // Force reload by clearing and setting src
      const src = this.srcValue
      this.iframeTarget.src = ''
      
      setTimeout(() => {
        this.iframeTarget.src = src
      }, 50)
    }
  }
  
  // State management methods
  showLoading() {
    debug('Showing loading state')
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
    
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
    
    if (this.hasIframeTarget) {
      this.iframeTarget.classList.add('hidden')
    }
  }
  
  showIframe() {
    debug('Showing iframe')
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
    
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
    
    if (this.hasIframeTarget) {
      this.iframeTarget.classList.remove('hidden')
    }
  }
  
  showError() {
    debug('Showing error state')
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
    
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
    
    if (this.hasIframeTarget) {
      this.iframeTarget.classList.add('hidden')
    }
  }
  
  clearAllTimers() {
    if (this.timeoutId) clearTimeout(this.timeoutId)
    if (this.shortDelayTimeout) clearTimeout(this.shortDelayTimeout)
    if (this.progressCheckTimeout) clearTimeout(this.progressCheckTimeout)
    
    this.timeoutId = null
    this.shortDelayTimeout = null
    this.progressCheckTimeout = null
  }

  // Retry button handler
  retry(event) {
    event.preventDefault()
    
    debug('Retry button clicked')
    this.startLoading()
  }
}
